import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Marker, useMap, useMapEvents } from 'react-leaflet';
import L from './leafletDrawAreaFix';
import { isLeafletChromeTarget, useIsDrawingMode } from './mapEventGuards';
import { ShareSheet } from '@/components/common/ShareSheet';
import { Button } from '@/components/ui/button';
import { directionsUrl, type SharePayload } from '@/utils/share';

interface LongPressPinProps {
  /** Turn the whole behaviour off without unmounting. */
  enabled?: boolean;
  /** Hold duration for the touch fallback. */
  holdMs?: number;
  /**
   * Page-specific actions for a dropped pin, rendered beside Remove pin —
   * e.g. the forecast page offers conditions at the pin, and the members
   * map offers the property the pin landed on.
   */
  actions?: (pin: [number, number]) => ReactNode;
}

const HINT_KEY = 'map-pin-hint:v1';
/** Ignore a second trigger this soon after the first — see `fire`. */
const DEDUPE_MS = 700;
/** Finger drift that turns a hold into a pan. */
const MOVE_TOLERANCE_PX = 10;

/**
 * Scratch pin, drawn dashed so it can't be mistaken for a wildfire marker.
 * Matches the inline-SVG approach used by WaterSourceOverlay.
 */
const pinIcon = L.divIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="34" viewBox="0 0 28 34">
      <defs><filter id="lpsh"><feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-opacity="0.3"/></filter></defs>
      <path d="M14 2C8.48 2 4 6.48 4 12c0 7.5 10 20 10 20s10-12.5 10-20C24 6.48 19.52 2 14 2z"
        fill="#5c6bc0" fill-opacity="0.9" filter="url(#lpsh)"
        stroke="white" stroke-width="1.5" stroke-dasharray="3 2"/>
      <circle cx="14" cy="12" r="3.5" fill="white"/>
    </svg>`,
  className: '',
  iconSize: [28, 34],
  iconAnchor: [14, 34],
  popupAnchor: [0, -36],
});

/**
 * Drop a throwaway pin anywhere on the map and share where it is.
 *
 * Mounted as a MapCanvas child rather than behind a new MapCanvas prop —
 * MapCanvas already renders arbitrary children inside its MapContainer, and
 * with 18 consumers every extra prop has a cost.
 *
 * Input handling is deliberately two-pronged. Leaflet's `contextmenu` covers
 * desktop right-click and Android's touch-hold in one handler, but iOS Safari
 * (particularly as an installed PWA) does not reliably fire it on the map
 * canvas — hence the pointer-timer fallback. Whichever arrives first wins;
 * `fire` de-dupes so a platform that emits both only drops one pin.
 *
 * The pin is never persisted. Nothing here talks to the API.
 */
export function LongPressPin({ enabled = true, holdMs = 500, actions }: LongPressPinProps) {
  const map = useMap();
  const isDrawing = useIsDrawingMode();
  const [pin, setPin] = useState<[number, number] | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const lastFiredRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const startPtRef = useRef<{ x: number; y: number } | null>(null);
  // Read in listeners bound once — reading the state directly would capture
  // whatever it was when the effect ran.
  const guardRef = useRef({ enabled, isDrawing, holdMs });
  guardRef.current = { enabled, isDrawing, holdMs };

  const fire = useCallback(
    (latlng: L.LatLng, target: EventTarget | null) => {
      const { enabled: on, isDrawing: drawing } = guardRef.current;
      if (!on || drawing) return;
      if (isLeafletChromeTarget(target)) return;
      const now = Date.now();
      if (now - lastFiredRef.current < DEDUPE_MS) return;
      lastFiredRef.current = now;
      setPin([latlng.lat, latlng.lng]);
    },
    [],
  );

  useMapEvents({
    contextmenu: (e) => {
      // Leaflet does not suppress the browser's own menu, so without this the
      // native context menu (or iOS's text callout) lands on top of the pin.
      L.DomEvent.preventDefault(e.originalEvent);
      fire(e.latlng, e.originalEvent?.target ?? null);
    },
  });

  // Touch fallback for platforms whose long-press never reaches `contextmenu`.
  useEffect(() => {
    if (!map) return undefined;
    const container = map.getContainer();

    const clear = () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      startPtRef.current = null;
    };

    const onPointerDown = (ev: PointerEvent) => {
      if (ev.pointerType === 'mouse' || !ev.isPrimary) return;
      const { enabled: on, isDrawing: drawing, holdMs: ms } = guardRef.current;
      if (!on || drawing) return;
      if (isLeafletChromeTarget(ev.target)) return;
      startPtRef.current = { x: ev.clientX, y: ev.clientY };
      const target = ev.target;
      timerRef.current = window.setTimeout(() => {
        const pt = startPtRef.current;
        clear();
        if (!pt) return;
        const rect = container.getBoundingClientRect();
        const latlng = map.containerPointToLatLng([pt.x - rect.left, pt.y - rect.top]);
        fire(latlng, target);
      }, ms);
    };

    const onPointerMove = (ev: PointerEvent) => {
      const pt = startPtRef.current;
      if (!pt) return;
      if (
        Math.abs(ev.clientX - pt.x) > MOVE_TOLERANCE_PX ||
        Math.abs(ev.clientY - pt.y) > MOVE_TOLERANCE_PX
      ) {
        clear();
      }
    };

    container.addEventListener('pointerdown', onPointerDown);
    container.addEventListener('pointermove', onPointerMove);
    container.addEventListener('pointerup', clear);
    container.addEventListener('pointercancel', clear);
    // A pan or zoom that starts mid-hold is not a long press.
    map.on('movestart', clear);
    map.on('zoomstart', clear);
    map.on('dragstart', clear);

    return () => {
      clear();
      container.removeEventListener('pointerdown', onPointerDown);
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', clear);
      container.removeEventListener('pointercancel', clear);
      map.off('movestart', clear);
      map.off('zoomstart', clear);
      map.off('dragstart', clear);
    };
  }, [map, fire]);

  // Right-click isn't discoverable; say so once, then never again.
  useEffect(() => {
    if (!enabled) return;
    try {
      if (localStorage.getItem(HINT_KEY)) return;
      localStorage.setItem(HINT_KEY, '1');
    } catch {
      /* storage blocked — just show it this once */
    }
    setShowHint(true);
    const id = window.setTimeout(() => setShowHint(false), 4000);
    return () => window.clearTimeout(id);
  }, [enabled]);

  if (!enabled) return null;

  const sharePayload: SharePayload | null = pin
    ? {
        title: 'Dropped pin',
        kind: 'Dropped pin',
        details: [
          ['Coordinates', `${pin[0].toFixed(5)}, ${pin[1].toFixed(5)}`],
          ['Directions', directionsUrl(pin[0], pin[1])],
        ],
        location: { lat: pin[0], lng: pin[1], label: 'Dropped pin' },
      }
    : null;

  return (
    <>
      {pin && (
        <Marker
          position={pin}
          icon={pinIcon}
          draggable
          eventHandlers={{
            click: () => setShareOpen(true),
            dragend: (e) => {
              const { lat, lng } = (e.target as L.Marker).getLatLng();
              setPin([lat, lng]);
            },
          }}
        />
      )}

      {showHint && (
        <div className="pointer-events-none absolute left-1/2 top-5 z-[460] -translate-x-1/2 rounded-full bg-foreground/85 px-4 py-2 text-xs font-medium text-background shadow-lg">
          Long-press (or right-click) the map to drop a pin
        </div>
      )}

      <ShareSheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        payload={sharePayload}
        showPreview={false}
        footer={
          <>
            {pin && actions?.(pin)}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setPin(null);
                setShareOpen(false);
              }}
            >
              Remove pin
            </Button>
          </>
        }
      />
    </>
  );
}

export default LongPressPin;
